import Decimal from "decimal.js";
import { DrawdownFund } from "features/drawdown/DrawdownFund";
import { AllocationLookupKey } from "gen/model/allocationLookupKey";
import {
  moneyFromDecimal,
  moneyToString,
  sumMoneyStrings,
} from "shared/utils/MoneyUtils";
import { useDeepCompareEffect } from "use-deep-compare";

export interface DrawdownCalculatorDataRow {
  useTypeId: string;
  allocationLookupKey: AllocationLookupKey;
  isProrata?: boolean;
  amount?: string;
  fundAmounts?: Record<string, string | null | undefined>;
}

export const useDrawdownCalculator = <T extends DrawdownCalculatorDataRow>(
  rows: T[],
  setRows: (rows: T[]) => void,
  fundsForRow?: (allocationLookupKey: AllocationLookupKey) => DrawdownFund[],
) => {
  useDeepCompareEffect(() => {
    if (fundsForRow) {
      setRows(computeAmounts(fundsForRow, rows));
    }
  }, [rows, fundsForRow, setRows]);
};

export const computeAmounts = <T extends DrawdownCalculatorDataRow>(
  allocationLookup: (
    allocationLookupKey: AllocationLookupKey,
  ) => DrawdownFund[],
  rows: T[],
): T[] =>
  rows.map((row): T => {
    if (!row.isProrata) {
      return {
        ...row,
        amount: sumMoneyStrings(Object.values(row.fundAmounts ?? {})),
      };
    }
    const funds = allocationLookup(row.allocationLookupKey);
    if (funds.length === 0) {
      return { ...row };
    }

    if (!row.amount) {
      return {
        ...row,
        fundAmounts: Object.fromEntries(funds.map((fund) => [fund.id, ""])),
      };
    }
    // this is to hanndle a case where the user starts entering a negative number
    const amount =
      row.amount === "-" ? new Decimal("0") : new Decimal(row.amount);
    // manually allocating amounts instead of using dinero allocate to match backend behavior and avoid penny rounding issues
    const allocations = funds
      .map((fund) =>
        moneyFromDecimal(
          fund.ownershipPercentage?.times(amount) ?? new Decimal(0),
        ),
      )
      .map(moneyToString);
    return {
      ...row,
      fundAmounts: Object.fromEntries(
        funds.map((fund, index) => [fund.id, allocations[index]]),
      ),
    };
  });
